import type { Asset, ValueChainStage } from "@/lib/assets";
import { LEVEL_LABELS } from "@/lib/level-badge";
import {
  HAZARD_LABELS,
  HORIZON_LABELS,
  hazardKeyLabel,
  hazardKeyOf,
  hazardKeysFor,
  hazardTypeOfKey,
  type AssetHazardResult,
  type HazardKey,
  type ResultStatus,
} from "@/lib/results";
import { filterAssets, getResultsForHorizon, orderedHorizons, type ResultsFilterState } from "@/lib/results-filters";
import { computeTakeaways, percentAtHighRiskPerHazard, type PerHazardHighRisk, type Takeaways } from "@/lib/takeaways";
import { HAZARD_TYPES, type ClimateScenario, type HazardType, type TimeHorizon } from "@shared/scoring/types";

const TOP_RISK_LIMIT = 10;
/** Importance at or above which an asset counts as business-critical (same cutoff takeaways.ts uses for "top"). */
const BUSINESS_CRITICAL_MIN = 3;

export type StatusCounts = Record<ResultStatus, number>;

export interface HazardShare extends PerHazardHighRisk {
  counts: StatusCounts;
}

export interface CaveatGroup {
  text: string;
  hazards: HazardType[];
}

export interface HorizonKpis {
  assetsAssessed: number;
  assetsTotal: number;
  businessCritical: number;
  hazardsAssessed: number;
  hazardsInScope: number;
}

export interface HorizonSection {
  horizon: TimeHorizon;
  label: string;
  /** This horizon's rows, already scoped to the filtered assets and in-scope hazards. */
  results: AssetHazardResult[];
  kpis: HorizonKpis;
  perHazard: HazardShare[];
  takeaways: Takeaways;
  statusCounts: StatusCounts;
  caveats: CaveatGroup[];
}

export interface TopRiskRow {
  assetId: string;
  assetName: string;
  stageName: string | null;
  importance: 1 | 2 | 3 | 4;
  /** One line per hazard, and per season for Changing Precipitation (see HazardKey). */
  key: HazardKey;
  hazardType: HazardType;
  maxRisk: 1 | 2 | 3 | 4;
  cells: Array<{ horizon: TimeHorizon; cell: AssetHazardResult | undefined }>;
}

export type ExportGate = { canExport: true } | { canExport: false; reason: string };

export interface ReportModel {
  workspaceName: string;
  /** null when no future horizon is selected. */
  scenario: ClimateScenario | null;
  horizons: TimeHorizon[];
  hazardsInScope: HazardType[];
  /** The summary lines of the hazards in scope, following the data: MAM +
   * OND for Changing Precipitation, plus a "main rainy season" line when
   * Malawi assets are in scope (see hazardKeysFor). Same in every section. */
  hazardKeys: HazardKey[];
  assets: Asset[];
  stages: ValueChainStage[];
  countries: string[];
  activeFilters: string[];
  sections: HorizonSection[];
  topRisks: TopRiskRow[];
  /** In-scope hazards with >= 1 computed row in any selected horizon. */
  assessedHazards: HazardType[];
  /** In-scope hazards with >= 1 computed future-horizon row (gates futureSourceNote). */
  hazardsWithComputedFuture: HazardType[];
  caveats: CaveatGroup[];
  summary: string[];
  exportGate: ExportGate;
}

export interface BuildReportInput {
  workspaceName: string;
  assets: Asset[];
  stages: ValueChainStage[];
  stageImportance: Record<string, number>;
  results: AssetHazardResult[];
  filters: ResultsFilterState;
}

function countStatuses(rows: AssetHazardResult[]): StatusCounts {
  const counts: StatusCounts = { computed: 0, pending: 0, no_data: 0, not_applicable: 0 };
  for (const r of rows) counts[r.status]++;
  return counts;
}

/** Why a hazard has no figure, in the same words the results UI uses. */
export function describeMissing(counts: StatusCounts): string {
  const reasons: string[] = [];
  if (counts.pending > 0) reasons.push("Not available yet");
  if (counts.no_data > 0) reasons.push("No data");
  if (counts.not_applicable > 0) reasons.push("Not at risk");
  return reasons.length ? reasons.join(" / ") : "No results";
}

/**
 * `x of y` is a true denominator because /api/results emits exactly one row per
 * asset × hazard × horizon × scenario, so a slice is always fully populated
 * (verified against results.routes.ts, 2026-09-20). If that ever becomes sparse,
 * the "no combinations at all" case would need to read differently from "none excluded".
 */
export function describeExclusions(counts: StatusCounts): string {
  const excluded = counts.pending + counts.no_data + counts.not_applicable;
  if (excluded === 0) return "No combinations are excluded.";
  const total = excluded + counts.computed;
  return (
    `${excluded} of ${total} asset–hazard combinations are excluded from all percentages ` +
    `(${counts.pending} not available yet, ${counts.no_data} no data, ${counts.not_applicable} not at risk).`
  );
}

/** Unique caveat texts (by field presence, never by hazard name), each with the hazards that carry it. */
function collectCaveats(computedRows: AssetHazardResult[]): CaveatGroup[] {
  const hazardsByText = new Map<string, Set<HazardType>>();
  for (const r of computedRows) {
    if (!r.scenarioCaveat) continue;
    const hazards = hazardsByText.get(r.scenarioCaveat) ?? new Set<HazardType>();
    hazards.add(r.hazardType);
    hazardsByText.set(r.scenarioCaveat, hazards);
  }
  return Array.from(hazardsByText, ([text, hazards]) => ({ text, hazards: HAZARD_TYPES.filter((h) => hazards.has(h)) }));
}

function mergeCaveats(groups: CaveatGroup[]): CaveatGroup[] {
  const hazardsByText = new Map<string, Set<HazardType>>();
  for (const g of groups) {
    const hazards = hazardsByText.get(g.text) ?? new Set<HazardType>();
    g.hazards.forEach((h) => hazards.add(h));
    hazardsByText.set(g.text, hazards);
  }
  return Array.from(hazardsByText, ([text, hazards]) => ({ text, hazards: HAZARD_TYPES.filter((h) => hazards.has(h)) }));
}

/** Human-readable list of the filters in effect, using the Results filters' own labels. */
export function summarizeActiveFilters(filters: ResultsFilterState, assets: Asset[], stages: ValueChainStage[]): string[] {
  const assetNameById = new Map(assets.map((a) => [a.id, a.name]));
  const stageNameById = new Map(stages.map((s) => [s.id, s.name]));
  const lines: string[] = [];
  if (filters.hazards.length) lines.push(`Hazards: ${filters.hazards.map((h) => HAZARD_LABELS[h]).join(", ")}`);
  if (filters.countries.length) lines.push(`Countries: ${filters.countries.join(", ")}`);
  if (filters.cities.length) lines.push(`Cities: ${filters.cities.join(", ")}`);
  if (filters.facilities.length) lines.push(`Facilities: ${filters.facilities.map((id) => assetNameById.get(id) ?? id).join(", ")}`);
  if (filters.assetImportance.length) lines.push(`Asset importance: ${filters.assetImportance.map((n) => LEVEL_LABELS[n]).join(", ")}`);
  if (filters.valueChainStageIds.length) {
    lines.push(`Value chain stage: ${filters.valueChainStageIds.map((id) => stageNameById.get(id) ?? id).join(", ")}`);
  }
  if (filters.valueChainImportance.length) lines.push(`Value chain importance: ${filters.valueChainImportance.join(", ")}`);
  return lines;
}

function buildSection(
  horizon: TimeHorizon,
  scenario: ClimateScenario,
  scopedResults: AssetHazardResult[],
  filteredAssets: Asset[],
  stageImportance: Record<string, number>,
  hazardsInScope: HazardType[],
  hazardKeys: HazardKey[],
): HorizonSection {
  const results = getResultsForHorizon(scopedResults, horizon, scenario);
  const computed = results.filter((r) => r.status === "computed");
  const perHazard: HazardShare[] = percentAtHighRiskPerHazard(results, hazardsInScope, hazardKeys).map((share) => ({
    ...share,
    counts: countStatuses(results.filter((r) => hazardKeyOf(r) === share.key)),
  }));

  return {
    horizon,
    label: HORIZON_LABELS[horizon],
    results,
    kpis: {
      assetsAssessed: new Set(computed.map((r) => r.assetId)).size,
      assetsTotal: filteredAssets.length,
      businessCritical: filteredAssets.filter((a) => a.businessImportance >= BUSINESS_CRITICAL_MIN).length,
      hazardsAssessed: new Set(computed.map((r) => r.hazardType)).size,
      hazardsInScope: hazardsInScope.length,
    },
    perHazard,
    takeaways: computeTakeaways(results, filteredAssets, stageImportance, hazardsInScope),
    statusCounts: countStatuses(results),
    caveats: collectCaveats(computed),
  };
}

function buildTopRisks(sections: HorizonSection[], filteredAssets: Asset[], stages: ValueChainStage[], hazardKeys: HazardKey[]): TopRiskRow[] {
  const stageNameById = new Map(stages.map((s) => [s.id, s.name]));
  const cellIndexByHorizon = sections.map((s) => new Map(s.results.map((r) => [`${r.assetId}|${hazardKeyOf(r)}`, r])));

  const rows: TopRiskRow[] = [];
  for (const asset of filteredAssets) {
    for (const key of hazardKeys) {
      const hazardType = hazardTypeOfKey(key);
      const cells = sections.map((s, i) => ({ horizon: s.horizon, cell: cellIndexByHorizon[i].get(`${asset.id}|${key}`) }));
      const computedRisks = cells.flatMap(({ cell }) => (cell?.status === "computed" && cell.risk !== undefined ? [cell.risk] : []));
      if (computedRisks.length === 0) continue; // never computed on any selected horizon
      rows.push({
        assetId: asset.id,
        assetName: asset.name,
        stageName: asset.valueChainStageId ? (stageNameById.get(asset.valueChainStageId) ?? null) : null,
        importance: asset.businessImportance,
        key,
        hazardType,
        maxRisk: Math.max(...computedRisks) as 1 | 2 | 3 | 4,
        cells,
      });
    }
  }

  rows.sort(
    (a, b) =>
      b.maxRisk - a.maxRisk ||
      b.importance - a.importance ||
      a.assetName.localeCompare(b.assetName) ||
      HAZARD_TYPES.indexOf(a.hazardType) - HAZARD_TYPES.indexOf(b.hazardType) ||
      a.key.localeCompare(b.key),
  );
  return rows.slice(0, TOP_RISK_LIMIT);
}

function buildSummary(sections: HorizonSection[]): string[] {
  return sections.map((s) => {
    const { assetsAssessed, assetsTotal, hazardsAssessed, hazardsInScope } = s.kpis;
    if (assetsAssessed === 0) return `${s.label}: no computed results yet.`;
    const lead = `${s.label}: ${assetsAssessed} of ${assetsTotal} assets assessed across ${hazardsAssessed} of ${hazardsInScope} hazards.`;
    const worst = s.takeaways.mostAssetsAtRisk;
    return worst
      ? `${lead} ${hazardKeyLabel(worst)} has the most assets at high risk.`
      : `${lead} No asset is at high risk (score 3 or 4) for any assessed hazard.`;
  });
}

function buildExportGate(filteredAssets: Asset[], sections: HorizonSection[]): ExportGate {
  if (filteredAssets.length === 0) return { canExport: false, reason: "No assets match the current filters." };
  if (!sections.some((s) => s.statusCounts.computed > 0)) {
    return { canExport: false, reason: "No computed results yet for the selected hazards and horizons." };
  }
  return { canExport: true };
}

/**
 * The single source of every number in the Tab 4 report. Pure: used by the
 * live preview and by the headless print route, so the two cannot disagree.
 * Contains no hazard-specific branches — status, not hazard name, drives every
 * count. Changing Precipitation's seasons are separate summary lines
 * (HazardKey) everywhere — MAM, OND, and one "main rainy season" line for
 * Malawi assets — never combined.
 */
export function buildReport(input: BuildReportInput): ReportModel {
  const { workspaceName, assets, stages, stageImportance, results, filters } = input;

  const horizons = orderedHorizons(filters.horizons);
  const hazardsInScope = filters.hazards.length ? HAZARD_TYPES.filter((h) => filters.hazards.includes(h)) : [...HAZARD_TYPES];
  const filteredAssets = filterAssets(assets, filters, stageImportance);
  const assetIds = new Set(filteredAssets.map((a) => a.id));
  const scopedResults = results.filter((r) => assetIds.has(r.assetId) && hazardsInScope.includes(r.hazardType));

  const hazardKeys = hazardKeysFor(hazardsInScope, scopedResults);

  const sections = horizons.map((h) =>
    buildSection(h, filters.scenario, scopedResults, filteredAssets, stageImportance, hazardsInScope, hazardKeys),
  );
  const computedRows = sections.flatMap((s) => s.results.filter((r) => r.status === "computed"));
  const computedFutureRows = sections.filter((s) => s.horizon !== "historical").flatMap((s) => s.results.filter((r) => r.status === "computed"));

  return {
    workspaceName,
    scenario: horizons.some((h) => h !== "historical") ? filters.scenario : null,
    horizons,
    hazardsInScope,
    hazardKeys,
    assets: filteredAssets,
    stages,
    countries: Array.from(new Set(filteredAssets.map((a) => a.country))).sort(),
    activeFilters: summarizeActiveFilters(filters, assets, stages),
    sections,
    topRisks: buildTopRisks(sections, filteredAssets, stages, hazardKeys),
    assessedHazards: hazardsInScope.filter((h) => computedRows.some((r) => r.hazardType === h)),
    hazardsWithComputedFuture: hazardsInScope.filter((h) => computedFutureRows.some((r) => r.hazardType === h)),
    caveats: mergeCaveats(sections.flatMap((s) => s.caveats)),
    summary: buildSummary(sections),
    exportGate: buildExportGate(filteredAssets, sections),
  };
}
